import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { SearchBar } from "../components/SearchBar";
import { TopicCard } from "../components/TopicCard";
import { Logo } from "../components/Logo";
import { DisclaimerModal } from "../components/DisclaimerModal";
import { FAQModal } from "../components/FAQModal";
import { HistoricalEvent } from "../components/HistoricalEvent";
import { MonthlyHighlights } from "../components/MonthlyHighlights";
import { useStore } from "../store";
import { FileText, HelpCircle } from "lucide-react";
import { supabase } from "../lib/supabase";
import { User } from "@supabase/supabase-js";
import EmailBanner from "../components/EmailBanner";

function ComparePage() {
  const { query, topics, loading, aiResponse, setIsFaqModalOpen } = useStore();
  const [isDisclaimerOpen, setIsDisclaimerOpen] = useState(false);
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const filteredTopics = topics.filter(topic =>
    topic.question.includes(query.trim())
  );

  const showAiResponse = query.trim() && (!filteredTopics.length || aiResponse);
  const showNoResults = query.trim() && !filteredTopics.length && !aiResponse && !loading;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex flex-col justify-between">
      <div>
        <EmailBanner />

        <nav className="bg-white shadow-sm">
          <div className="max-w-6xl mx-auto px-4 py-3 flex justify-between items-center">
            <Link to="/" className="text-xl font-bold text-gray-800">
              מתי כדאי?
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/blog" className="text-gray-600 hover:text-gray-800">בלוג</Link>
              <Link to="/admin" className="text-gray-600 hover:text-gray-800">ניהול</Link>
            </div>
          </div>
        </nav>

        <div className="py-12 px-4">
          <div className="max-w-6xl mx-auto">
            <Logo />
            <div className="text-center mb-12">
              <p className="text-gray-600 text-lg">השוואות, תחזיות ורעיונות – במקום אחד</p>
            </div>

            <div className="flex justify-center items-center gap-4 mb-8">
              <SearchBar />
            </div>

            <HistoricalEvent />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {!query && <MonthlyHighlights />}
              {!showAiResponse && filteredTopics.map(topic => (
                <TopicCard key={topic.id} topic={topic} />
              ))}
              {showAiResponse && aiResponse && (
                <div className="md:col-span-2">
                  <TopicCard topic={aiResponse} />
                </div>
              )}
            </div>

            {showNoResults && (
              <div className="text-center mt-12">
                <p className="text-gray-600 text-lg">מחפש תשובה בעזרת בינה מלאכותית...</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <footer className="bg-white border-t text-center text-sm text-gray-600 py-6 mt-8">
        <div className="flex justify-center gap-4 flex-wrap">
          <Link to="/about" className="hover:underline">אודות</Link>
          <span>|</span>
          <Link to="/contact" className="hover:underline">צור קשר</Link>
          <span>|</span>
          <Link to="/privacy" className="hover:underline">מדיניות פרטיות</Link>
          <span>|</span>
          <Link to="/terms" className="hover:underline">תנאי שימוש</Link>
        </div>
      </footer>

      <div className="fixed right-4 bottom-4 flex flex-col gap-2">
        <Link to="/smart-search">
          <button className="bg-purple-600 text-white p-3 rounded-full shadow-lg hover:bg-purple-700">
            🔍
          </button>
        </Link>
        <button onClick={() => setIsFaqModalOpen(true)} className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800 transition-colors bg-white/80 hover:bg-white px-3 py-2 rounded-lg shadow-sm hover:shadow-md">
          <HelpCircle className="w-4 h-4" />
          <span>שאלות נפוצות</span>
        </button>
        <button onClick={() => setIsDisclaimerOpen(true)} className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800 transition-colors bg-white/80 hover:bg-white px-3 py-2 rounded-lg shadow-sm hover:shadow-md">
          <FileText className="w-4 h-4" />
          <span>כתב ויתור ותנאי שימוש</span>
        </button>
      </div>

      <DisclaimerModal isOpen={isDisclaimerOpen} onClose={() => setIsDisclaimerOpen(false)} />
      <FAQModal />
    </div>
  );
}

export default ComparePage;
